import { useEffect, useMemo, useState } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  ListChecks,
  ScanFace,
  UserCheck,
  ShieldAlert,
  Building2,
  Smartphone,
  GitBranch,
  RefreshCw,
  FileJson,
  Download,
  Trash2,
  Plus,
  Workflow as WorkflowIcon,
  type LucideIcon,
} from 'lucide-react'
import { useOpsCenterStore } from '../store'
import { buildGraph, layout } from '../graph'
import { exportWorkflowJson } from '../io'
import { appendStep, appendBranch, removeTask, findTaskById } from './taskOps'
import { nodeTypes } from './nodes/nodeTypes'
import { JsonPanel } from './JsonPanel'
import { NodeDrawer } from './drawer/NodeDrawer'
import { SequenceDiagramView } from './SequenceDiagramView'
import type { StepName } from '../types'

export interface WorkflowEditorProps {
  workflowId: string
}

const PALETTE: { stepName: StepName; label: string; icon: LucideIcon; color: string }[] = [
  { stepName: 'MATCHLIST', label: 'Matchlist', icon: ListChecks, color: '#a78bfa' },
  { stepName: 'IDV', label: 'IDV', icon: ScanFace, color: '#818cf8' },
  { stepName: 'KYC', label: 'KYC', icon: UserCheck, color: '#38bdf8' },
  { stepName: 'AML', label: 'AML', icon: ShieldAlert, color: '#fb7185' },
  { stepName: 'KYB', label: 'KYB', icon: Building2, color: '#2dd4bf' },
  { stepName: 'FRAUD', label: 'Fraud & Device Risk', icon: Smartphone, color: '#fb923c' },
]

function WorkflowEditorInner({ workflowId }: WorkflowEditorProps) {
  const workflow = useOpsCenterStore((s) => s.workflows.find((w) => w.id === workflowId))
  const updateTasks = useOpsCenterStore((s) => s.updateTasks)
  const selectedNodeId = useOpsCenterStore((s) => s.selectedNodeId)
  const selectNode = useOpsCenterStore((s) => s.selectNode)

  const [showJson, setShowJson] = useState(false)
  const [view, setView] = useState<'canvas' | 'sequence'>('canvas')
  const [paletteOpen, setPaletteOpen] = useState(false)

  // The canvas is DERIVED from `wf.tasks` — the store is the source of truth
  // for structure. Re-run buildGraph (dagre layout included) whenever tasks
  // change; never derive structure back out of React Flow's own state.
  const graph = useMemo(
    () => (workflow ? buildGraph(workflow.tasks) : { nodes: [], edges: [] }),
    [workflow?.tasks],
  )

  const [nodes, setNodes, onNodesChange] = useNodesState(graph.nodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(graph.edges)

  // Re-sync local RF state whenever the derived graph changes (i.e. whenever
  // wf.tasks changes). Cosmetic dragging between syncs is preserved because
  // useNodesState/useEdgesState own the live positions in between renders.
  useEffect(() => {
    setNodes(graph.nodes)
    setEdges(graph.edges)
  }, [graph, setNodes, setEdges])

  if (!workflow) {
    return (
      <div className="rounded-lg border border-[#1e2c44] bg-[#0f1729] p-6">
        <p className="text-xs text-[#5b6b85]">Workflow not found.</p>
      </div>
    )
  }

  const wf = workflow

  function handleAddStep(stepName: StepName) {
    updateTasks(workflowId, appendStep(wf.tasks, stepName))
  }

  function handleAddBranch() {
    updateTasks(workflowId, appendBranch(wf.tasks))
  }

  function handleRelayout() {
    const relaid = layout({ nodes, edges })
    setNodes(relaid.nodes)
  }

  function handleDeleteNode() {
    if (!selectedNodeId) return
    updateTasks(workflowId, removeTask(wf.tasks, selectedNodeId))
    selectNode(null)
  }

  function handleExport() {
    const data = exportWorkflowJson(wf)
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${wf.name.replace(/\s+/g, '_').toLowerCase()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const selectedTask = selectedNodeId ? findTaskById(wf.tasks, selectedNodeId) : undefined
  const canDelete = Boolean(selectedTask)

  return (
    <div className="flex flex-col gap-3">
      {/* toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-[#1e2c44] bg-[#0f1729] px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-sm font-semibold text-[var(--text-primary)] truncate">{wf.name}</h2>
          <span className="text-[10px] font-mono text-[#5b6b85] shrink-0">v{wf.version}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {view === 'canvas' && (
            <button
              onClick={() => setPaletteOpen((o) => !o)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                paletteOpen ? 'bg-[#6366f1] text-white border-[#6366f1]' : 'text-[#94a3b8] border-[#1e2c44] hover:bg-[#1a2740]'
              }`}
            >
              <Plus size={12} /> Add node
            </button>
          )}
          {canDelete && (
            <button
              onClick={handleDeleteNode}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-red-400 border border-red-500/30 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 size={12} /> Delete node
            </button>
          )}
          <button
            onClick={handleRelayout}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-[#94a3b8] border border-[#1e2c44] hover:bg-[#1a2740] transition-colors"
          >
            <RefreshCw size={12} /> Re-layout
          </button>
          <button
            onClick={() => setShowJson((v) => !v)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              showJson
                ? 'bg-[#6366f1] text-white border-[#6366f1]'
                : 'text-[#94a3b8] border-[#1e2c44] hover:bg-[#1a2740]'
            }`}
          >
            <FileJson size={12} /> JSON
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium text-[#94a3b8] border border-[#1e2c44] hover:bg-[#1a2740] transition-colors"
          >
            <Download size={12} /> Export JSON
          </button>
          <button
            onClick={() => setView((v) => (v === 'sequence' ? 'canvas' : 'sequence'))}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
              view === 'sequence'
                ? 'bg-[#6366f1] text-white border-[#6366f1]'
                : 'text-[#94a3b8] border-[#1e2c44] hover:bg-[#1a2740]'
            }`}
          >
            <WorkflowIcon size={12} /> Sequence Diagram
          </button>
        </div>
      </div>

      {/* node palette — a horizontal popup panel so options have room to breathe */}
      {view === 'canvas' && paletteOpen && (
        <div className="rounded-lg border border-[#1e2c44] bg-[#0f1729] p-3">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-[10px] uppercase tracking-wide text-[#5b6b85]">Add node</p>
            <span className="text-[10px] text-[#5b6b85]">— appended to the end of the workflow</span>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            {PALETTE.map(({ stepName, label, icon: Icon, color }) => (
              <button
                key={stepName}
                onClick={() => handleAddStep(stepName)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-[var(--text-primary)] border border-[#1e2c44] hover:bg-[#1a2740] transition-colors"
              >
                <Icon size={14} style={{ color }} /> {label}
              </button>
            ))}
            <button
              onClick={handleAddBranch}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-[var(--text-primary)] border border-[#1e2c44] hover:bg-[#1a2740] transition-colors"
            >
              <GitBranch size={14} style={{ color: '#eab308' }} /> Branch
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        {/* canvas or sequence diagram */}
        {view === 'canvas' ? (
          <div className="flex-1 min-w-0 rounded-lg border border-[#1e2c44] bg-[#0a1120] h-[640px] overflow-hidden">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              onNodeClick={(_, n) => selectNode(n.id)}
              onPaneClick={() => selectNode(null)}
              fitView
              proOptions={{ hideAttribution: true }}
            >
              <Background />
              <Controls />
              <MiniMap />
            </ReactFlow>
          </div>
        ) : (
          <SequenceDiagramView workflowId={workflowId} />
        )}

        {/* right rail: JSON panel when toggled on, otherwise the per-node
            config drawer (reads `selectedNodeId` from the store itself and
            renders nothing when there's no selection). */}
        <div className="w-[480px] shrink-0 h-[640px]">
          {showJson ? (
            <JsonPanel wf={wf} />
          ) : selectedNodeId ? (
            <NodeDrawer />
          ) : (
            <div className="h-full rounded-lg border border-dashed border-[#1e2c44]" />
          )}
        </div>
      </div>
    </div>
  )
}

export function WorkflowEditor(props: WorkflowEditorProps) {
  return (
    <ReactFlowProvider>
      <WorkflowEditorInner {...props} />
    </ReactFlowProvider>
  )
}
